import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Users, RefreshCw, Download } from 'lucide-react';

export default function IkasBuyingSegment() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const refresh = async () => {
    try {
      setLoading(true);
      const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
      if (!brandId) { setRows([]); return; }
      // Demo fallback: synthesize brand purchase segments for demo brand
      const DEMO_BRAND_ID = 'ec9873c2-0edb-44eb-ac6f-2670c8e56dc4';
      if (brandId === DEMO_BRAND_ID) {
        const mkRowsForBrand = (brand, tiers) => {
          // tiers: { ge4, ge3Only, ge2Only, ge1Only } counts (mutually exclusive layers)
          // Will produce customers with order totals meeting thresholds
          const out = [];
          const month = new Date().toISOString().slice(0, 10);
          let seq = 1;
          // 4+ orders
          for (let i = 0; i < tiers.ge4; i++) {
            out.push({ month, customer_id: `${brand}-C${String(seq++).padStart(4, '0')}`, brand_name: brand, orders_count: 4 });
          }
          // exactly 3 orders
          for (let i = 0; i < tiers.ge3Only; i++) {
            out.push({ month, customer_id: `${brand}-C${String(seq++).padStart(4, '0')}`, brand_name: brand, orders_count: 3 });
          }
          // exactly 2 orders
          for (let i = 0; i < tiers.ge2Only; i++) {
            out.push({ month, customer_id: `${brand}-C${String(seq++).padStart(4, '0')}`, brand_name: brand, orders_count: 2 });
          }
          // exactly 1 order
          for (let i = 0; i < tiers.ge1Only; i++) {
            out.push({ month, customer_id: `${brand}-C${String(seq++).padStart(4, '0')}`, brand_name: brand, orders_count: 1 });
          }
          return out;
        };
        const demoRows = [
          ...mkRowsForBrand('Brand A', { ge4: 20, ge3Only: 20, ge2Only: 40, ge1Only: 40 }), // ge1=120 ge2=80 ge3=40 ge4=20
          ...mkRowsForBrand('Brand B', { ge4: 12, ge3Only: 13, ge2Only: 35, ge1Only: 40 }), // 100, 60, 25, 12
          ...mkRowsForBrand('Brand C', { ge4: 8, ge3Only: 12, ge2Only: 30, ge1Only: 40 }),  // 90, 50, 20, 8
          // Additional demo brands to increase examples
          ...mkRowsForBrand('Brand D', { ge4: 16, ge3Only: 22, ge2Only: 44, ge1Only: 48 }), // 130, 82, 38, 16
          ...mkRowsForBrand('Brand E', { ge4: 10, ge3Only: 18, ge2Only: 38, ge1Only: 44 }), // 110, 66, 28, 10
          ...mkRowsForBrand('Brand F', { ge4: 14, ge3Only: 16, ge2Only: 34, ge1Only: 40 }), // 104, 64, 30, 14
          ...mkRowsForBrand('Brand G', { ge4: 9,  ge3Only: 14, ge2Only: 28, ge1Only: 36 }), // 87, 51, 23, 9
          ...mkRowsForBrand('Brand H', { ge4: 6,  ge3Only: 12, ge2Only: 24, ge1Only: 30 }), // 72, 42, 18, 6
          ...mkRowsForBrand('Brand I', { ge4: 5,  ge3Only: 10, ge2Only: 22, ge1Only: 28 }), // 65, 37, 15, 5
          ...mkRowsForBrand('Brand J', { ge4: 4,  ge3Only: 8,  ge2Only: 18, ge1Only: 24 })  // 54, 30, 12, 4
        ];
        setRows(demoRows);
        return;
      }
      const headers = { 'X-Brand-ID': brandId };
      const base = (typeof window !== 'undefined' && window.location.hostname === 'localhost') ? '' : 'https://api.glowytics.com';
      const res = await fetch(`${base}/api/ikas/customers/brand-monthly`, { credentials: 'include', headers });
      const js = await res.json().catch(() => ({}));
      const data = res.ok && js?.rows ? js.rows : [];
      setRows(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const brandSegments = useMemo(() => {
    // rows: [{ month, customer_id, brand_name, orders_count }]
    const byBrand = new Map();
    for (const r of rows || []) {
      const brand = r.brand_name || 'Unknown';
      const cid = r.customer_id;
      const orders = Number(r.orders_count || 0);
      if (!cid) continue;
      if (!byBrand.has(brand)) byBrand.set(brand, new Map());
      const perCust = byBrand.get(brand);
      perCust.set(cid, (perCust.get(cid) || 0) + orders);
    }
    // Compute thresholds per brand and collect customer IDs
    const result = [];
    for (const [brand, perCust] of byBrand.entries()) {
      let ge1 = 0, ge2 = 0, ge3 = 0, ge4 = 0;
      const idsGe1 = [], idsGe2 = [], idsGe3 = [], idsGe4 = [];
      for (const [cid, total] of perCust.entries()) {
        if (total >= 1) { ge1++; idsGe1.push(cid); }
        if (total >= 2) { ge2++; idsGe2.push(cid); }
        if (total >= 3) { ge3++; idsGe3.push(cid); }
        if (total >= 4) { ge4++; idsGe4.push(cid); }
      }
      result.push({
        brand,
        counts: { ge1, ge2, ge3, ge4 },
        ids: { ge1: idsGe1, ge2: idsGe2, ge3: idsGe3, ge4: idsGe4 }
      });
    }
    // Sort descending by ge1 to show most popular first
    result.sort((a, b) => (b.counts.ge1 - a.counts.ge1) || a.brand.localeCompare(b.brand));
    return result;
  }, [rows]);

  const exportIdsCsv = useCallback((brand, tier, customerIds) => {
    const csvRows = ['customer_id'];
    for (const id of customerIds) csvRows.push(`${id}`);
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const safeBrand = brand.replace(/[^a-zA-Z0-9_-]/g, '_');
    a.download = `${safeBrand}_${tier}_customers.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 relative">
      {loading ? (
        <div className="absolute inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center z-10">
          <div className="flex items-center gap-3 text-gray-700">
            <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm">Loading...</span>
          </div>
        </div>
      ) : null}

      {/* Header */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/ikaslogoicon.png"
              alt="Ikas"
              className="w-10 h-10 object-contain rounded-lg"
            />
            <div>
              <h1 className="text-xl font-bold text-gray-900">Brand Purchase Behavior</h1>
              <p className="text-sm text-gray-500">
                These cards group customers by how many purchases they have made from the same brand.
                The number in each card represents the count of customers who meet that threshold
                (e.g., "At least 2 orders" = customers who purchased from that brand at least twice).
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              className={`p-2 text-gray-600 rounded-xl hover:bg-gray-100 transition-colors ${loading ? 'opacity-50' : ''}`}
              onClick={refresh}
              title="Refresh"
              disabled={loading}
            >
              <RefreshCw size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Brand list with segments */}
      <div className="bg-white border rounded-2xl p-4 shadow-sm">
        {brandSegments.length === 0 ? (
          <div className="text-sm text-gray-500">No data.</div>
        ) : (
          <div className="space-y-4">
            {brandSegments.map(({ brand, counts, ids }) => (
              <div key={brand} className="border rounded-xl p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-semibold text-gray-900">{brand}</div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { label: 'At least 1 order', tier: 'ge1' },
                    { label: 'At least 2 orders', tier: 'ge2' },
                    { label: 'At least 3 orders', tier: 'ge3' },
                    { label: 'At least 4 orders', tier: 'ge4' },
                  ].map(({ label, tier }) => (
                    <div key={tier} className="border rounded-lg p-3 bg-indigo-50/40">
                      <div className="flex items-start justify-between">
                        <div className="text-xs text-gray-600">{label}</div>
                        <button
                          className="text-gray-400 hover:text-indigo-600 transition-colors p-0.5 rounded hover:bg-indigo-100/60"
                          onClick={() => exportIdsCsv(brand, tier, ids[tier])}
                          title={`Export ${label} customer IDs`}
                        >
                          <Download size={13} />
                        </button>
                      </div>
                      <div className="text-xl font-bold text-gray-900 mt-1">{counts[tier].toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


